import { IAddress } from '@commenda/types';
import { cn } from '@commenda/utils';
import { Country } from '@prisma/client';
import { State } from 'country-state-city';
import { CheckIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CountryInput } from '../CountryInput';
import { FieldWrapper } from '../FieldWrapper';
import { Input } from '../Input';
import { InputErrorMessage } from '../InputErrorMessage';
import { Label } from '../Label';
import { SingleCombobox } from '../SingleCombobox';

type DisabledState = Partial<Record<keyof IAddress, boolean>> | boolean;

const isFieldDisabled = (
  disabled: DisabledState,
  field: keyof IAddress,
  hasCountrySelected: boolean
): boolean => {
  // If no country is selected, disable all fields except country
  if (!hasCountrySelected && field !== 'country') {
    return true;
  }

  // Then check the existing disabled logic
  if (typeof disabled === 'boolean') {
    return disabled;
  }

  return disabled?.[field] ?? false;
};

const EMPTY_ADDRESS: IAddress = {
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  state: '',
  country: '',
};

const USStatesToBeRemoved = [
  'AS', // American Samoa
  'UM-81', // Baker Island
  'GU', // Guam
  'UM-84', // Howland Island
  'UM-86', // Jarvis Island
  'UM-67', // Johnston Atoll
  'UM-89', // Kingman Reef
  'UM-71', // Midway Atoll
  'UM-76', // Navassa Island
  'MP', // Northern Mariana Islands
  'UM-95', // Palmyra Atoll
  'PR', // Puerto Rico
  'UM', // United States Minor Outlying Islands
  'VI', // United States Virgin Islands
  'UM-79', // Wake Island
];

const AddressInput = ({
  id,
  disabled = false,
  address,
  onChange,
  lastRowClassName,
  error,
  defaultCountry,
  className,
  onClearError,
  readonly = false,
  hideLabels = false,
  stateOptions,
}: {
  disabled?: DisabledState;
  address: IAddress;
  onChange: (address: IAddress) => void;
  lastRowClassName?: string;
  id?: string;
  error?: Partial<Record<keyof IAddress, string | undefined>>;
  defaultCountry?: Country;
  className?: string;
  onClearError?: (field: keyof IAddress) => void;
  readonly?: boolean;
  hideLabels?: boolean;
  /**
   * Optional backend-driven state options. When provided, the state combobox is
   * populated from these instead of the country-state-city library, and an empty
   * array hides the state field — used by the location forms so the picker only
   * offers subdivision codes the sales-tax validator accepts. Leaving it
   * `undefined` (the default) preserves library-driven behavior for every other
   * consumer of this component.
   */
  stateOptions?: { text: string; value: string; label: string }[];
}) => {
  const [localAddress, setLocalAddress] = useState<IAddress>(EMPTY_ADDRESS);

  useEffect(() => {
    if (address)
      setLocalAddress(
        address.country ? address : { ...address, country: defaultCountry ?? '' }
      );
    else setLocalAddress(EMPTY_ADDRESS);
  }, [address, defaultCountry]);

  // Backend-driven options (when passed) take precedence over the
  // country-state-city library.
  const isBackendDrivenStates = stateOptions !== undefined;
  const states = isBackendDrivenStates
    ? stateOptions
    : State.getStatesOfCountry(localAddress.country || defaultCountry)
        .filter((state) => {
          if (localAddress.country === Country.US) {
            return !USStatesToBeRemoved.includes(state.isoCode); // For US we have a lot of values coming from the third party which are not states, so filter them out.
          }
          return true;
        })
        .map((state) => ({
          text: state.name,
          value: state.isoCode,
          label: state.name,
        }));

  // When state options are backend-driven and the country has no curated list,
  // hide the state field entirely. Library-driven (default) always renders it.
  const showStateField = !isBackendDrivenStates || states.length > 0;

  const onChangeAddressField = (field: keyof IAddress, value: string) => {
    const updatedAddress = {
      ...EMPTY_ADDRESS,
      ...localAddress,
      [field]: value,
    };

    const previousCountry = localAddress.country || defaultCountry;
    if (field === 'country' && value !== previousCountry) {
      updatedAddress.state = '';
      if (error?.state && onClearError) {
        onClearError('state');
      }
    }

    setLocalAddress(updatedAddress);
    onChange(updatedAddress);
    if (error?.[field] && onClearError) {
      onClearError(field);
    }
  };

  const getFieldError = (field: keyof IAddress): string | undefined => {
    return error?.[field];
  };

  // Check if a country is selected (either from defaultCountry or localAddress)
  const hasCountrySelected = Boolean(
    defaultCountry ||
      (localAddress.country && localAddress.country.trim() !== '')
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <FieldWrapper _className="gap-sm">
        {!hideLabels && (
          <Label
            htmlFor={
              id ? `${id}-address-country-input` : 'address-country-input'
            }
          >
            Country
          </Label>
        )}
        <CountryInput
          readonly={readonly}
          id={id ? `${id}-address-country-input` : 'address-country-input'}
          value={
            (localAddress.country && localAddress.country.trim() !== '')
              ? (localAddress.country as Country)
              : ((defaultCountry ?? localAddress.country) as Country)
          }
          type="single"
          onValueChange={(ctr) => onChangeAddressField('country', ctr!)}
          disabled={isFieldDisabled(disabled, 'country', hasCountrySelected)}
          error={getFieldError('country')}
        />
      </FieldWrapper>
      <FieldWrapper _className="gap-sm">
        {!hideLabels && (
          <Label
            htmlFor={id ? `${id}-address-line-1-input` : 'address-line-1-input'}
          >
            Address line 1
          </Label>
        )}
        <Input
          readOnly={readonly}
          id={id ? `${id}-address-line-1-input` : 'address-line-1-input'}
          placeholder="Address line 1"
          name="addressLine1"
          onChange={(e) => onChangeAddressField('addressLine1', e.target.value)}
          disabled={isFieldDisabled(
            disabled,
            'addressLine1',
            hasCountrySelected
          )}
          value={localAddress.addressLine1}
          error={getFieldError('addressLine1')}
        />
      </FieldWrapper>
      <FieldWrapper _className="gap-sm">
        {!hideLabels && (
          <Label
            htmlFor={id ? `${id}-address-line-2-input` : 'address-line-2-input'}
          >
            Address line 2
          </Label>
        )}
        <Input
          readOnly={readonly}
          placeholder="Address line 2"
          name="addressLine2"
          onChange={(e) => onChangeAddressField('addressLine2', e.target.value)}
          disabled={isFieldDisabled(
            disabled,
            'addressLine2',
            hasCountrySelected
          )}
          value={localAddress.addressLine2}
          error={getFieldError('addressLine2')}
        />
      </FieldWrapper>
      <FieldWrapper _className="gap-sm">
        {!hideLabels && (
          <Label
            htmlFor={id ? `${id}-address-city-input` : 'address-city-input'}
          >
            City
          </Label>
        )}
        <Input
          readOnly={readonly}
          placeholder="City"
          name="city"
          onChange={(e) => onChangeAddressField('city', e.target.value)}
          disabled={isFieldDisabled(disabled, 'city', hasCountrySelected)}
          value={localAddress.city}
          error={getFieldError('city')}
        />
      </FieldWrapper>
      <FieldWrapper _className="gap-sm">
        {!hideLabels && (
          <Label
            htmlFor={
              id
                ? `${id}-address-postal-code-input`
                : 'address-postal-code-input'
            }
          >
            Postal code
          </Label>
        )}
        <Input
          readOnly={readonly}
          placeholder="Postal code"
          className="h-11"
          name="postalCode"
          maxLength={20}
          onChange={(e) => onChangeAddressField('postalCode', e.target.value)}
          disabled={isFieldDisabled(disabled, 'postalCode', hasCountrySelected)}
          value={localAddress.postalCode}
          error={getFieldError('postalCode')}
        />
      </FieldWrapper>
      {showStateField && (
        <FieldWrapper _className="gap-sm">
          {!hideLabels && (
            <Label
              htmlFor={id ? `${id}-address-state-input` : 'address-state-input'}
            >
              State
            </Label>
          )}
          <SingleCombobox
            readonly={readonly}
            id={id ? `${id}-address-state-input` : 'address-state-input'}
            disabled={
              isFieldDisabled(disabled, 'state', hasCountrySelected) || readonly
            }
            value={localAddress.state || ''}
            setValue={(value) => onChangeAddressField('state', value)}
            options={states}
            placeholder="Select state"
            error={getFieldError('state')}
            notFoundText="No state found."
            hideSearch={states.length < 5}
            renderTrigger={({ selectedOption }) => (
              <div className="flex justify-between items-center w-full">
                <div className="text-text-primary txt-small-regular">
                  {selectedOption?.label}
                </div>
              </div>
            )}
            renderOption={({ isSelected, option }) => {
              return (
                <div className="flex px-1.5 justify-between items-center w-full overflow-hidden text-ellipsis">
                  <div className="txt-small-regular text-text-secondary">
                    {option.label}
                  </div>
                  {isSelected ? <CheckIcon className="h-4 w-4 shrink-0" /> : null}
                </div>
              );
            }}
          />
          {getFieldError('state') && (
            <InputErrorMessage message={getFieldError('state')!} />
          )}
        </FieldWrapper>
      )}
    </div>
  );
};

export { AddressInput, USStatesToBeRemoved };
